import { IDictionary, mobile } from '@bimser/core';
import IconLanguage from '@bimser/icons/16/language';
import * as React from 'react';
import { BCButton, BCSpaceCompact, BCTranslatePopover } from '../';
import BCInput, { BCInputRef, IInputChangeEventArgs } from '../BCInput';
import { findMessage } from '../BCIntl';
import BCLabel from '../BCLabel';
import BCPopover from '../BCPopover';
import BCScrollbars from '../BCScrollbars';
import Style from './assets/style.scss';
import {
	IInputMultiLanguageRunTranslateProps,
	IInputMultiLanguageTranslate,
	IProps
} from './entities';

const inputStyle: React.CSSProperties = { width: 'calc(100% - 35px)' };
const inputFullStyle: React.CSSProperties = { width: '100%' };
const buttonStyle: React.CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '35px',
	fontWeight: 900,
	fontSize: '14px'
};
const scrollStyle: React.CSSProperties = { height: '100%', width: '100%' };
const overlayStyle: React.CSSProperties = { maxWidth: 425 };
const labelStyle: React.CSSProperties = { fontSize: 13, width: 'unset' };

const BCInputMultiLanguage = (props: IProps) => {
	const input = React.useRef<BCInputRef>(null);
	const buttonRef = React.useRef<any>(null);

	const [isFocus, setIsFocus] = React.useState<boolean>(false);
	const [focusEvent, setFocusEvent] = React.useState<any>(null);
	const [value, setValue] = React.useState<IDictionary<string>>(props.values || {});

	React.useEffect(() => {
		document.addEventListener('click', onBlurMLInput, true);
		return () => {
			document.removeEventListener('click', onBlurMLInput, true);
			toggleScrollOnVisible(false);
		};
	}, []);

	React.useEffect(() => {
		if (!isFocus) {
			props.onBlur?.(focusEvent, value);
		}
	}, [isFocus]);

	const onBlurMLInput = (event: MouseEvent | Event) => {
		const targetEvent = event.target as Node;
		const inputMLPopover = document.querySelector('[class*="inputMLPopoverContainer"]');
		const translatePopover = document.querySelector('[class*="translatePopoverContainer"]');

		const isEventInsideInputPopover = inputMLPopover?.contains(targetEvent);
		const isEventInsideTranslatePopover = translatePopover?.contains(targetEvent);
		const isEventInsideInput = input?.current?.input.contains(targetEvent);
		const isEventInsideMLButton = buttonRef?.current?.contains(event.target);

		if (isEventInsideMLButton) {
			setFocusEvent(event);
		}

		if (isEventInsideInput || isEventInsideInputPopover || isEventInsideTranslatePopover) {
			setIsFocus(true);
		}
	};

	const renderPopoverContent = () => (
		<div className={Style.MLPopover}>
			<BCScrollbars styles={scrollStyle}>
				<div className={Style.pdR5}>
					{props.supportedLanguages.map(item => {
						if (item.id != props.currentLanguage.id) {
							return (
								<div
									className={Style.popoverRow}
									key={item.name}
								>
									<div className="ant-col-7 ant-col-offset-1 ant-form-item-label">
										<label title={item.name}>{item.name}</label>
									</div>
									<div className="ant-col-15 ant-col-offset-1 ">
										<BCInput
											itemKey={item.culture}
											onChange={onLanguageValueChange}
											value={props.values?.[item.culture]}
											regExp={props.regExp}
											size="small"
											maxLength={props.maxLength}
											seleniumId={props.seleniumId}
											placeHolder={props.innerInputProps?.placeHolders?.getText(
												item.culture
											)}
											{...(props.innerInputProps || {})}
										/>
									</div>
								</div>
							);
						}
					})}
				</div>
			</BCScrollbars>
		</div>
	);

	const onLanguageValueChange = (e: IInputChangeEventArgs) => {
		const newValue = { ...value, [e.itemKey]: e.data };
		setValue(newValue);
		props.onLanguageValueChange?.(e.itemKey, e.data);
		props.onChange?.(newValue);
	};

	const disableScrollEvent = React.useCallback((e: Event) => {
		e.preventDefault();
		e.stopPropagation();
		return false;
	}, []);

	const toggleScrollOnVisible = (visible: boolean) => {
		if (mobile().any()) {
			visible
				? document.addEventListener('touchmove', disableScrollEvent, { passive: false })
				: document.removeEventListener('touchmove', disableScrollEvent);
		} else {
			const allScrollableArea = document.querySelectorAll<HTMLDivElement>(
				'div[class*="bcScrollableArea"]'
			);

			allScrollableArea.forEach(scrollable => {
				if (scrollable.contains(input.current?.input)) {
					['wheel', 'scroll'].forEach(eventKey => {
						visible
							? scrollable.addEventListener(eventKey, disableScrollEvent, true)
							: scrollable.removeEventListener(eventKey, disableScrollEvent, true);
					});

					scrollable.childNodes.forEach((child: HTMLDivElement) => {
						if (child.className.includes('scrollbarTrack')) {
							child.style.pointerEvents = visible ? 'none' : '';
						}
					});
				}
			});
		}
	};

	const onVisibilityChange = (visible: boolean) => {
		toggleScrollOnVisible(visible);
		setIsFocus(visible);

		if (props.onVisibilityChange) {
			props.onVisibilityChange(visible);
		}
	};

	const renderPopoverTitle = () => {
		const title = props.title || (
			<BCLabel customStyles={labelStyle}>{findMessage.get('100775')}</BCLabel>
		);

		return props.translate?.enabled ? (
			<div className={Style.popoverTitle}>
				<BCLabel customStyles={labelStyle}>{title}</BCLabel>
				{renderTranslatePopover()}
			</div>
		) : (
			title
		);
	};

	const onFocusInput = (args: IInputChangeEventArgs) => {
		setIsFocus(true);
		setFocusEvent(args.senderArgs);
		props.onFocus?.();
	};

	const onBlurInput = (args: React.FocusEvent<HTMLInputElement>) => {
		setIsFocus(false);
		setFocusEvent(args);
	};

	const onKeyDown = (e: React.KeyboardEvent<Element>) => {
		if (e.key === 'Tab') {
			setIsFocus(false);
		}
	};

	const renderInput = () => {
		//Custom input
		if (props.renderInput) {
			return (
				<div style={props.disableMultiLanguage ? inputFullStyle : inputStyle}>
					{props.renderInput()}
				</div>
			);
		}

		return (
			<BCInput
				ref={input}
				autoFocus={props.autoFocus}
				onKeyDown={onKeyDown}
				onFocus={onFocusInput}
				onBlur={onBlurInput}
				onPressEnter={props.onPressEnter}
				placeHolder={props.placeHolder}
				itemKey={props.currentLanguage.culture}
				onChange={onLanguageValueChange}
				value={props.values?.[props.currentLanguage.culture]}
				size={props.size}
				regExp={props.regExp}
				style={props.disableMultiLanguage ? inputFullStyle : inputStyle}
				maxLength={props.maxLength}
				disabled={props.disabled}
				readOnly={props.readOnly}
				seleniumId={props.seleniumId}
				{...(props.innerInputProps || {})}
			/>
		);
	};

	const renderTranslatePopover = () => (
		<BCTranslatePopover
			values={props.values}
			nullCheck
			isLoading={props.translate.loading}
			onClick={(target: string, source: string) =>
				props.translate.runTranslate({ target, source, values: props.values })
			}
			canTranslate={props.translate.canTranslate}
		/>
	);

	if (props.disableMultiLanguage) {
		return renderInput();
	}

	return (
		<BCSpaceCompact
			block
			size={props.size}
		>
			{renderInput()}
			<BCPopover
				content={renderPopoverContent()}
				title={renderPopoverTitle()}
				trigger={props.trigger || 'click'}
				placement={props.placement || 'leftTop'}
				overlayStyle={{ ...overlayStyle, ...props.overlayStyle }}
				onOpenChange={onVisibilityChange}
				overlayClassName={Style.inputMLPopoverContainer}
				getPopupContainer={props.getPopupContainer}
			>
				<BCButton
					buttonRef={buttonRef}
					style={buttonStyle}
					icon={<IconLanguage />}
					size={props.size}
					disabled={props.disabled || props.readOnly}
					cssClass={props.buttonClassName}
				/>
			</BCPopover>
		</BCSpaceCompact>
	);
};
export default BCInputMultiLanguage;
export {
	IProps as IBCInputMultiLanguageProps,
	IInputMultiLanguageRunTranslateProps,
	IInputMultiLanguageTranslate
};
